import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Toolbar,
    Badge,
    LinearProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    Storage as StorageIcon,
    Description as DescriptionIcon,
    AccountTree as AccountTreeIcon,
    CloudDownload as CloudDownloadIcon,
    CloudUpload as CloudUploadIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import toastr from 'toastr';
import * as LegacyViewerService from '../../../services/clinicPortalServices/legacyViewerService';
import EntityDataViewer from './EntityDataViewer';
import DataRelationships from './DataRelationships';
import GlobalSearch from './GlobalSearch';
import { formatBytes, formatNumber } from '../../../services/common/util';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`legacy-tabpanel-${index}`}
            aria-labelledby={`legacy-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const LegacyViewer = () => {
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [entities, setEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEntities, setFilteredEntities] = useState([]);
    const [lastScanTime, setLastScanTime] = useState(null);
    const [populationStatus, setPopulationStatus] = useState({});
    const [populatingEntities, setPopulatingEntities] = useState(new Set());

    useEffect(() => {
        loadEntityList();
        loadPopulationStatus();
    }, []);

    useEffect(() => {
        if (searchTerm) {
            const filtered = entities.filter((entity) =>
                entity.entityType.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredEntities(filtered);
        } else {
            setFilteredEntities(entities);
        }
    }, [searchTerm, entities]);

    const loadEntityList = async () => {
        try {
            setLoading(true);
            const response = await LegacyViewerService.getEntityList();
            if (response.success) {
                setEntities(response.data);
                setFilteredEntities(response.data);

                // Check last scan time from localStorage
                const scanTime = localStorage.getItem('legacyViewerLastScan');
                if (scanTime) {
                    setLastScanTime(new Date(scanTime));
                }
            }
        } catch (error) {
            toastr.error('Failed to load entity list');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadPopulationStatus = async () => {
        try {
            const response = await LegacyViewerService.getEntityPopulationStatus();
            if (response.success) {
                const statusMap = {};
                response.data.forEach((item) => {
                    statusMap[item.entityType] = item;
                });
                setPopulationStatus(statusMap);
            }
        } catch (error) {
            console.error('Failed to load population status:', error);
        }
    };

    const handlePopulateEntity = async (entity, e) => {
        e.stopPropagation();

        if (populatingEntities.has(entity.entityType)) {
            toastr.warning('Already populating this entity');
            return;
        }

        try {
            setPopulatingEntities((prev) => new Set([...prev, entity.entityType]));
            toastr.info(`Started populating ${entity.entityType} data...`);

            const response = await LegacyViewerService.populateEntityData(entity.entityType);
            if (response.success) {
                toastr.success(`Population started for ${entity.entityType}. This may take a few minutes.`);

                // Poll for status updates
                const pollInterval = setInterval(async () => {
                    await loadPopulationStatus();
                    const status = populationStatus[entity.entityType];
                    if (status && status.populationPercentage === 100) {
                        clearInterval(pollInterval);
                        setPopulatingEntities((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(entity.entityType);
                            return newSet;
                        });
                        toastr.success(`${entity.entityType} population completed!`);
                    }
                }, 5000); // Check every 5 seconds

                // Stop checking after 10 minutes
                setTimeout(() => clearInterval(pollInterval), 600000);
            }
        } catch (error) {
            toastr.error(`Failed to populate ${entity.entityType}: ${error.message}`);
            setPopulatingEntities((prev) => {
                const newSet = new Set(prev);
                newSet.delete(entity.entityType);
                return newSet;
            });
        }
    };

    const handleScanFiles = async () => {
        try {
            setScanning(true);
            toastr.info('Scanning and storing all CSV data. This may take several minutes...');
            const response = await LegacyViewerService.scanFiles(true); // Store full data by default
            if (response.success) {
                if (response.totalRowsStored) {
                    toastr.success(
                        `Scanned ${response.filesScanned} files and stored ${response.totalRowsStored.toLocaleString()} rows successfully`
                    );
                } else {
                    toastr.success(`Scanned and stored ${response.filesScanned} files with all data successfully`);
                }
                const now = new Date();
                setLastScanTime(now);
                localStorage.setItem('legacyViewerLastScan', now.toISOString());
                await loadEntityList();
                await loadPopulationStatus();
            }
        } catch (error) {
            toastr.error(`Failed to scan files: ${error.message || 'Unknown error'}`);
            console.error('Scan files error:', error);
            console.error('Error response:', error.response);
        } finally {
            setScanning(false);
        }
    };

    const handleEntityClick = (entity) => {
        setSelectedEntity(entity);
        setTabValue(1);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const getTotalStats = () => {
        const stats = entities.reduce(
            (acc, entity) => {
                acc.totalFiles += entity.fileCount || 0;
                acc.totalRows += entity.totalRows || 0;
                acc.totalSize += entity.totalSize || 0;
                return acc;
            },
            { totalFiles: 0, totalRows: 0, totalSize: 0 }
        );

        return stats;
    };

    const renderEntityCard = (entity) => {
        const dateRange =
            entity.earliestDate && entity.latestDate
                ? `${new Date(entity.earliestDate).toLocaleDateString()} - ${new Date(entity.latestDate).toLocaleDateString()}`
                : 'No date range';

        const status = populationStatus[entity.entityType] || {};
        const isPopulated = status.isPopulated || false;
        const isPopulating = populatingEntities.has(entity.entityType);
        const populationPercentage = status.populationPercentage || 0;

        return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={entity.entityType}>
                <Card
                    sx={{
                        height: '100%',
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s'
                        }
                    }}
                    onClick={() => handleEntityClick(entity)}
                >
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                            <StorageIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6" component="div" noWrap>
                                {entity.entityType.replace(/_/g, ' ')}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Files: <strong>{entity.fileCount}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Rows: <strong>{formatNumber(entity.totalRows)}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Size: <strong>{formatBytes(entity.totalSize)}</strong>
                            </Typography>
                            {(isPopulated || isPopulating) && (
                                <Box mt={1}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Population Status:
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={populationPercentage}
                                        sx={{ mb: 0.5 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {formatNumber(status.populatedRows || 0)} / {formatNumber(entity.totalRows)}{' '}
                                        rows ({populationPercentage}%)
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Typography variant="caption" color="text.secondary" display="block">
                            {dateRange}
                        </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEntityClick(entity);
                            }}
                        >
                            View Data
                        </Button>
                        {isPopulated ? (
                            <Chip size="small" label="Populated" color="success" icon={<CheckCircleIcon />} />
                        ) : (
                            <Button
                                size="small"
                                startIcon={isPopulating ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                onClick={(e) => handlePopulateEntity(entity, e)}
                                disabled={isPopulating}
                                color="primary"
                                variant="outlined"
                            >
                                {isPopulating ? 'Populating...' : 'Populate'}
                            </Button>
                        )}
                    </CardActions>
                </Card>
            </Grid>
        );
    };

    const stats = getTotalStats();

    return (
        <div className="clinic-contain">
            <div className="breadcrumb-bar">
                <div className="container-fluid">
                    <div className="row align-items-center">
                        <div className="col-md-12 col-12">
                            <nav aria-label="breadcrumb" className="page-breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Admin
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Legacy Data Viewer
                                    </li>
                                </ol>
                            </nav>
                            <h2 className="breadcrumb-title">Legacy Data Viewer</h2>
                        </div>
                    </div>
                </div>
            </div>
            <div className="px-5 py-3">
                {/* Summary Stats */}
                <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                        <Typography variant="h4" component="h1">
                            Legacy Data Viewer
                        </Typography>
                        <Box>
                            {lastScanTime && (
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                                    Last scan: {lastScanTime.toLocaleString()}
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                startIcon={scanning ? <CircularProgress size={20} /> : <RefreshIcon />}
                                onClick={handleScanFiles}
                                disabled={scanning}
                            >
                                {scanning ? 'Scanning...' : 'Scan Files'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
                <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                            <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Total Entities
                                </Typography>
                                <Typography variant="h5">{entities.length}</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                            <Badge badgeContent={stats.totalFiles} color="primary" max={999}>
                                <StorageIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                            </Badge>
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Total Files
                                </Typography>
                                <Typography variant="h5">{formatNumber(stats.totalRows)} rows</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                            <CloudDownloadIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                            <Box>
                                <Typography color="text.secondary" variant="body2">
                                    Total Data Size
                                </Typography>
                                <Typography variant="h5">{formatBytes(stats.totalSize)}</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Tabs */}
                <Paper sx={{ width: '100%' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="legacy viewer tabs">
                        <Tab icon={<StorageIcon />} label="Entities" />
                        <Tab icon={<VisibilityIcon />} label="Data Viewer" disabled={!selectedEntity} />
                        <Tab icon={<AccountTreeIcon />} label="Relationships" />
                        <Tab icon={<SearchIcon />} label="Global Search" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        {/* Search Bar */}
                        <Toolbar sx={{ pl: 0, pr: 0 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search entities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                ×
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Toolbar>

                        {/* Entity Cards */}
                        {loading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : filteredEntities.length === 0 ? (
                            <Alert severity="info">
                                {entities.length === 0
                                    ? 'No entities found. Please scan files first.'
                                    : 'No entities match your search.'}
                            </Alert>
                        ) : (
                            <Grid container spacing={3}>
                                {filteredEntities.map(renderEntityCard)}
                            </Grid>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        {selectedEntity && <EntityDataViewer entity={selectedEntity} onBack={() => setTabValue(0)} />}
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <DataRelationships />
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <GlobalSearch />
                    </TabPanel>
                </Paper>
            </div>
        </div>
    );
};

export default LegacyViewer;
